import React, { useEffect, useState } from "react";
import { Avatar, Box, Button, Chip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import KeyIcon from "@mui/icons-material/Key";
import StarIcon from "@mui/icons-material/Star";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { MediaSlider } from "../../common/MediaSlider";
import {
  fetchBusinessById,
  followBusiness,
  isFollowingBusiness,
  unfollowBusiness,
} from "../../../services/businessService";
import { logCapsuleFollow } from "../../../utils/measurement";

function resolveBusinessAvatar(business, sourceCapsule) {
  return String(
    business?.avatarUrl ||
      business?.logoUrl ||
      business?.profileImage ||
      business?.photoURL ||
      business?.pinUrl ||
      sourceCapsule?.businessAvatarUrl ||
      ""
  ).trim();
}

function resolveBusinessName(business, sourceCapsule) {
  return String(
    business?.displayName ||
      business?.companyName ||
      business?.name ||
      sourceCapsule?.placeName ||
      ""
  ).trim();
}

/**
 * Renders the original featured capsule referenced by a forwarded envelope,
 * below the sender's personal note — with full parity to a native featured
 * capsule (business identity, Follow, content, media). The CTA renders in the
 * page's persistent CTA bar, matching direct featured views.
 *
 * - available + not gated: full featured content + Follow
 * - available + gated (memberExclusive, non-insider): Insider upsell,
 *   business identity only — no media/body (FR-A6)
 * - unavailable: friendly "no longer available" card (FR-A7)
 */
export default function ForwardedFeaturedSection({
  status,
  sourceCapsule,
  isGated = false,
  onUpgrade,
  onOpenLightbox,
  uid = "",
  onRequireSignIn,
  onNotify,
}) {
  const [business, setBusiness] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  const businessId = String(
    sourceCapsule?.businessId || sourceCapsule?.senderId || ""
  ).trim();

  useEffect(() => {
    let cancelled = false;
    if (!businessId || status !== "available") {
      setBusiness(null);
      return undefined;
    }
    (async () => {
      try {
        const loaded = await fetchBusinessById(businessId);
        if (!cancelled) setBusiness(loaded || null);
      } catch {
        if (!cancelled) setBusiness(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, status]);

  useEffect(() => {
    let cancelled = false;
    if (!businessId || !uid || status !== "available") {
      setIsFollowing(false);
      return undefined;
    }
    (async () => {
      try {
        const following = await isFollowingBusiness(uid, businessId);
        if (!cancelled) setIsFollowing(Boolean(following));
      } catch {
        if (!cancelled) setIsFollowing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, uid, status]);

  const notify = (message, type) => onNotify?.({ open: true, message, type });

  const handleToggleFollow = async () => {
    if (!uid) {
      onRequireSignIn?.();
      return;
    }
    if (!businessId || followBusy) return;
    setFollowBusy(true);
    try {
      if (isFollowing) {
        await unfollowBusiness(uid, businessId);
        setIsFollowing(false);
        // unfollow — attributed to the original featured capsule's campaign.
        logCapsuleFollow(sourceCapsule, false);
        notify("Unfollowed business.", "info");
      } else {
        await followBusiness(uid, {
          id: businessId,
          displayName: resolveBusinessName(business, sourceCapsule),
          companyName: business?.companyName || "",
          avatarUrl: resolveBusinessAvatar(business, sourceCapsule),
        });
        setIsFollowing(true);
        // follow — attributed to the original featured capsule's campaign.
        logCapsuleFollow(sourceCapsule, true);
        notify("Now following business.", "success");
      }
    } catch (err) {
      console.error("Failed to toggle follow for forwarded featured business:", err);
      notify("Could not update follow status.", "error");
    } finally {
      setFollowBusy(false);
    }
  };

  if (status !== "available" && status !== "unavailable") return null;

  if (status === "unavailable") {
    return (
      <Box
        data-testid="forwarded-featured-unavailable"
        sx={(theme) => ({
          px: 2,
          py: 2,
          mt: 2,
          borderRadius: 1,
          border: `1px dashed ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.text.primary, 0.03),
        })}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          This featured capsule is no longer available.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The campaign has ended — but the note above is yours to keep.
        </Typography>
      </Box>
    );
  }

  const avatarUrl = resolveBusinessAvatar(business, sourceCapsule);
  const businessName = resolveBusinessName(business, sourceCapsule);

  const header = (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Avatar
          src={avatarUrl || undefined}
          alt=""
          sx={(theme) => ({
            width: 40,
            height: 40,
            bgcolor:
              theme.palette.mode === "dark"
                ? theme.palette.primary.light
                : theme.palette.primary.main,
            color: theme.palette.getContrastText(
              theme.palette.mode === "dark"
                ? theme.palette.primary.light
                : theme.palette.primary.main
            ),
          })}
        >
          <StarIcon fontSize="small" />
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {businessName ? (
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
              {businessName}
            </Typography>
          ) : null}
          <Chip
            icon={<StarIcon sx={{ fontSize: 14 }} />}
            label="Featured"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 22, fontWeight: 700 }}
          />
        </Box>
        {isFollowing ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckRoundedIcon />}
            onClick={handleToggleFollow}
            disabled={followBusy}
            sx={(theme) => ({
              flex: "0 0 auto",
              minWidth: 112,
              px: 1.35,
              py: 0.7,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              lineHeight: 1.2,
              borderColor: alpha(theme.palette.success.main, 0.58),
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.success.light
                  : theme.palette.success.dark,
              backgroundColor: alpha(theme.palette.success.main, 0.08),
              "&:hover": {
                borderColor: alpha(theme.palette.success.main, 0.76),
                backgroundColor: alpha(theme.palette.success.main, 0.12),
              },
              "& .MuiButton-startIcon": { mr: 0.35, ml: -0.25 },
            })}
          >
            Following
          </Button>
        ) : null}
      </Box>

      {!isFollowing ? (
        <Box
          sx={(theme) => ({
            mt: 1.85,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 1.25,
            py: 1.35,
            borderRadius: 1,
            border: `1px solid ${alpha(
              theme.palette.success.main,
              theme.palette.mode === "dark" ? 0.32 : 0.24
            )}`,
            backgroundColor: alpha(
              theme.palette.success.main,
              theme.palette.mode === "dark" ? 0.1 : 0.07
            ),
          })}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
            <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
              Stay in the loop
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25, lineHeight: 1.25 }}
            >
              New drops from this business appear in Following.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={handleToggleFollow}
            disabled={followBusy}
            sx={(theme) => ({
              flex: "0 0 auto",
              minWidth: 92,
              px: 1.6,
              py: 0.9,
              fontWeight: 800,
              color: theme.palette.getContrastText(theme.palette.success.main),
              backgroundColor: theme.palette.success.main,
              "&:hover": { backgroundColor: alpha(theme.palette.success.main, 0.9) },
              "& .MuiButton-startIcon": { mr: 0.35, ml: -0.25 },
            })}
          >
            Follow
          </Button>
        </Box>
      ) : null}
    </Box>
  );

  if (isGated) {
    return (
      <Box
        data-testid="forwarded-featured-gated"
        sx={(theme) => {
          const isDark = theme.palette.mode === "dark";
          const accent = isDark ? theme.palette.primary.light : theme.palette.primary.main;
          return {
            px: 2,
            py: 2,
            mt: 2,
            borderRadius: 1,
            border: `1px solid ${alpha(accent, isDark ? 0.5 : 0.25)}`,
            backgroundColor: alpha(accent, isDark ? 0.16 : 0.06),
          };
        }}
      >
        {header}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, mb: 1.5 }}>
          <KeyIcon fontSize="small" color="primary" />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            This featured capsule is for Insider members. Join Insider to unlock it.
          </Typography>
        </Box>
        <Button
          variant="contained"
          fullWidth
          onClick={onUpgrade}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Join Insider
        </Button>
      </Box>
    );
  }

  const media = Array.isArray(sourceCapsule?.media) ? sourceCapsule.media : [];
  const messageTitle = String(sourceCapsule?.messageTitle || "").trim();
  const messageBody = String(sourceCapsule?.messageBody || "").trim();

  return (
    <Box
      data-testid="forwarded-featured-content"
      sx={(theme) => {
        const isDark = theme.palette.mode === "dark";
        const accent = isDark ? theme.palette.primary.light : theme.palette.primary.main;
        return {
          px: 2,
          py: 2,
          mt: 2,
          borderRadius: 1,
          border: `1px solid ${alpha(accent, isDark ? 0.5 : 0.25)}`,
          backgroundColor: alpha(accent, isDark ? 0.12 : 0.04),
        };
      }}
    >
      {header}
      {messageTitle ? (
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}>
          {messageTitle}
        </Typography>
      ) : null}
      {messageBody ? (
        <Typography
          variant="body2"
          sx={{ mt: messageTitle ? 0 : 1.5, mb: media.length ? 1.5 : 0, whiteSpace: "pre-wrap" }}
        >
          {messageBody}
        </Typography>
      ) : null}
      {media.length > 0 && (
        <Box sx={{ mt: messageTitle || messageBody ? 0 : 1.5 }}>
          <MediaSlider items={media} isLocked={false} onOpenLightbox={onOpenLightbox} />
        </Box>
      )}
    </Box>
  );
}
